<template>
  <div>
    <div id="navigation-bar-container">
      <ul id="navigation">
        <li
          v-for="step in steps"
          id="nav-list"
          :key="step.id"
          :class="{
            active: step.id === props.currentStep,
            completed: step.id < props.currentStep,
          }"
          @click="stepClicked(step, currentStep)"
        >
          <br />
          {{ step.name }}
        </li>
      </ul>
    </div>
  </div>
</template>
<script setup lang="ts">
import { computed, ComputedRef, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import router from "@/router";
import { useStore } from "vuex";
import { RouteName } from "@/apps/merchant3/router/RouteName";
import MerchantFlags from "@/apps/merchant3/types/MerchantFlags";
import useIncodeWebFlow from "@/apps/merchant3/hooks/useIncodeWebFlow";
import { NavigationStep } from "@/apps/merchant3/types/NavigationStep";
import { isEcommerceFlow } from "@/apps/merchant3/helpers/checkoutFlows";
import useCartPersistState from "@/apps/merchant3/hooks/useCartPersistState";
import { SessionStorageEnum } from "@/apps/merchant3/enums/sessionStorageEnum";

const props = defineProps({
  currentStep: {
    type: Number,
    default: 1,
  },
});

const isIncodeWebFlow = useIncodeWebFlow();

const { t } = useI18n();
const store = useStore();

const flags = store.state.merchant?.merchantFlags;

let step1Code: RouteName;
let step2Code: RouteName;

if (flags?.includes(MerchantFlags.USE_QUICK_APPROVAL_FLOW)) {
  step1Code = RouteName.M3CustomerRegistration;
} else if (isIncodeWebFlow()) {
  step1Code = RouteName.M3CustomerContactInfoView;
} else {
  step1Code = RouteName.M3IdentificationUploader;
}

if (flags?.includes(MerchantFlags.NEW_OFFERS_UI)) {
  step2Code = RouteName.M3PreparingOffersV2;
} else {
  step2Code = RouteName.M3Offers;
}

const steps: ComputedRef<Array<NavigationStep>> = computed(() => {
  return isEcommerceFlow.value
    ? [
        {
          id: 1,
          code: RouteName.M3TermsAndConditions,
          name: t("SHARED.NAVIGATION_STEPS.IDENTITY"),
        },
        {
          id: 2,
          code: step2Code,
          name: t("SHARED.NAVIGATION_STEPS.PRICING"),
        },
        {
          id: 3,
          code: RouteName.M3EcommerceSummary,
          name: t("SHARED.NAVIGATION_STEPS.CONTRACT"),
        },
        {
          id: 4,
          code: RouteName.M3EcommerceSelectDownpaymentType,
          name: t("SHARED.NAVIGATION_STEPS.PAYMENT_OPTIONS"),
        },
      ]
    : [
        {
          id: 1,
          code: step1Code,
          name: t("SHARED.NAVIGATION_STEPS.REGISTER"),
        },
        {
          id: 2,
          code: step2Code,
          name: t("SHARED.NAVIGATION_STEPS.PRICING"),
        },
        {
          id: 3,
          code: RouteName.M3Contract,
          name: t("SHARED.NAVIGATION_STEPS.CONTRACT"),
        },
        {
          id: 4,
          code: RouteName.M3Software,
          name: t("SHARED.NAVIGATION_STEPS.SOFTWARE"),
        },
        {
          id: 5,
          code: RouteName.M3Complete,
          name: t("SHARED.NAVIGATION_STEPS.CHECKOUT"),
        },
      ];
});

const stepClicked = (step: NavigationStep, currentStep: number) => {
  if (step.id < currentStep) {
    router
      .push({
        name: step.code,
      })
      .then(() => {
        clearStoreAndNavigateHome();
      });
  }
};
const clearStoreAndNavigateHome = () => {
  setTimeout(() => router.go(0), 1);
  return false;
};
onMounted(() => {
  // Skip cart persist state if no cart exists (e.g., D2C flow before cart creation)
  const hasCart =
    store.state.carts.cartTag ||
    sessionStorage.getItem(SessionStorageEnum.CartTag);

  if (hasCart) {
    useCartPersistState(() => true);
  }
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
#navigation-bar-container {
  position: relative;
  background-color: $pj-color-nav-bar-gray;
  background-size: 30px 10px;
  border-color: $pj-color-nav-bar-gray;
  z-index: 1;
  width: 100%;
  margin-bottom: 1%;
}
#navigation {
  margin: 0; /** this removes default margin of li **/
  padding: 0; /** this removes default margin of li **/
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
}

#navigation li {
  display: inline-block;
  list-style: none;
  vertical-align: middle;
  font-family: "Nunito", sans-serif !important;
  font-weight: bold;
  font-size: 2.8vw;
  position: relative;
  text-align: center;
  margin: 0 auto 5px auto;
  flex-basis: 0;
  flex-grow: 1;
  box-sizing: border-box;
}
#navigation li:before {
  margin: 5px;
  font-size: 1.25em;
  font-family: "Font Awesome\ 5 Free", serif;
  display: inline-block;
  content: "\f192";
  color: #d0d5dc;
  background-color: $pj-color-nav-bar-gray;
  font-weight: 100;
  position: relative;
  z-index: 3;
}
#navigation li:after {
  content: "";
  position: absolute;
  width: 100%;
  height: 1.5px;
  background-color: #d0d5dc;
  top: 30%;
  left: -50%;
}
#navigation li.active:before {
  content: "\f192";
  color: green;
  font-weight: 100;
}
#navigation li.completed:before {
  content: "\f058";
  color: green;
  font-weight: 900;
}
#navigation li:first-child:after {
  content: none;
}

@media all and (min-width: 768px) {
  #navigation li {
    font-size: 14px;
  }
  #navigation-bar-container {
    padding-left: 25%;
    padding-right: 25%;
  }
  #navigation {
    width: 100%;
  }
  #navigation li.completed:before {
    cursor: pointer;
  }
}
</style>
